/**
 * Reusable meal rating popup.
 *
 * Usage:
 *   MealRatingPopup.show({
 *     order: { order_id, order_name, customer_id, delivery_date },
     *     meals: [{ product_id, variant_id, sku, title, subtitle, image, type, quantity }],
 *     onConfirm: function(payload) {},
 *     onClose: function() {}
 *   });
 *   MealRatingPopup.hide();
 *
 * Confirm POSTs action=save_batch to design_your_plan/meal_rating, then
 * passes the payload to onConfirm. Order History inline save is unchanged.
 *
 * Helper for Order History:
 *   MealRatingPopup.getLastOrderFromAccountPage()
 */
(function () {
  var SHOP_DOMAIN = 'fuelhub.myshopify.com';

  var DEFAULT_TITLE = 'RATE YOUR RECENT ORDER';
  var DEFAULT_SUBTITLE = 'Help us improve your next box by rating your recent meals';
  var ALL_SET_TITLE = "YOU'RE ALL SET";
  var ALL_SET_SUBTITLE = 'This is your latest order review';

  var THUMB_UP_SVG =
    '<svg width="24" height="23" viewBox="0 0 24 23" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M21.711 8.07704H13.8484L12.1202 1.5993C11.8112 0.644322 10.9413 0 9.93419 0H6.11159V8.07704H0V23H18.1631C19.0787 23 19.9027 22.4477 20.2461 21.6538L23.8169 14.1981C23.9428 13.9105 24 13.5998 24 13.2891V10.3897C24 9.12406 22.97 8.08854 21.711 8.08854V8.07704ZM6.11159 20.6989H2.28898V10.3782H6.11159V20.6989ZM18.1631 20.6989H8.40057V2.27814L9.92275 2.25513L12.0858 10.3782H21.711L21.7339 13.2431L18.1631 20.7104V20.6989Z" fill="currentColor"/>' +
    '</svg>';

  var THUMB_DOWN_SVG =
    '<svg width="24" height="23" viewBox="0 0 24 23" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M24 0H5.83691C4.92132 0 4.08584 0.552276 3.75393 1.34617L0.183119 8.82491C0.0572246 9.11256 0 9.41171 0 9.72236V12.6218C0 13.8874 1.03004 14.923 2.28898 14.923H10.1516L11.8684 21.3892C12.166 22.3442 13.0472 23 14.0544 23H17.877V14.923H23.9886V0H24ZM15.5994 20.7219L14.0773 20.7449L11.9142 12.6218H2.28898L2.26609 9.75688L5.83691 2.28964H15.5994V20.7219ZM21.711 12.6218H17.8884V2.30115H21.711V12.6218Z" fill="currentColor"/>' +
    '</svg>';

  function mealKey(meal, index) {
    return String(meal.product_id || meal.variant_id || index);
  }

  /**
   * Product titles reach us HTML-encoded (e.g. "HONEY &amp; CHILLI BEEF") from both
   * Liquid-injected JSON and data-title attributes. Textarea content is parsed as
   * text, so this decodes entities without executing markup.
   */
  function formatDeliveryDate(value) {
    if (!value) return '';

    var text = String(value).trim();
    var day;
    var month;
    var year;
    var match = text.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);

    if (match) {
      // Stored as DD-MM-YYYY on order attributes (e.g. 16-08-2026).
      day = parseInt(match[1], 10);
      month = parseInt(match[2], 10);
      year = parseInt(match[3], 10);
    } else {
      return text;
    }

    var months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    var monthName = months[month - 1];
    if (!monthName || !day || !year) return text;

    return day + ' ' + monthName + ' ' + year;
  }

  function decodeHtmlEntities(value) {
    if (value === null || value === undefined) return '';
    var text = String(value);
    if (text.indexOf('&') === -1) return text;

    var textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  function normalizeMeal(meal) {
    var normalized = {};
    Object.keys(meal || {}).forEach(function (key) {
      normalized[key] = meal[key];
    });
    normalized.title = decodeHtmlEntities(normalized.title);
    normalized.subtitle = decodeHtmlEntities(normalized.subtitle);

    if (!normalized.subtitle && normalized.title.indexOf(' - ') !== -1) {
      var parts = normalized.title.split(' - ');
      normalized.title = parts.shift();
      normalized.subtitle = parts.join(' - ');
    }

    if (normalized.subtitle === 'Default Title') normalized.subtitle = '';
    return normalized;
  }

  function getAuthFields() {
    var customer = (window.dyp && window.dyp.shopify && window.dyp.shopify.customer) || {};
    return {
      type: 'shopify',
      hash: customer.hash || '',
      id: customer.id || '',
      email: customer.email || '',
      shop: SHOP_DOMAIN
    };
  }

  function getMealRatingUrl() {
    if (!window.dyp || !window.dyp.apiUrl) {
      console.warn('[MealRatingApi] window.dyp.apiUrl is missing');
      return '';
    }
    return window.dyp.apiUrl + 'meal_rating';
  }

  function payloadToFormData(payload) {
    var formData = new FormData();
    Object.keys(payload || {}).forEach(function (key) {
      var value = payload[key];
      if (value === undefined || value === null) return;
      if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    });
    return formData;
  }

  function postMealRating(payload) {
    var url = getMealRatingUrl();
    if (!url) {
      return Promise.reject(new Error('Meal rating API URL is missing'));
    }

    console.log('[MealRatingApi] POST', payload && payload.action, payload);

    return fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      body: payloadToFormData(payload)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error('Meal rating request failed');
          error.status = response.status;
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  function withOverallRate(payload) {
    var data = {};
    Object.keys(payload || {}).forEach(function (key) {
      data[key] = payload[key];
    });
    if (!data.rate) {
      if (data.bad_count > 0) data.rate = 'bad';
      else if (data.good_count > 0) data.rate = 'good';
      else data.rate = '';
    }
    return data;
  }

  function saveBatch(payload) {
    var data = withOverallRate(payload);
    data.action = 'save_batch';
    return postMealRating(data);
  }

  function saveComments(payload, comments) {
    var data = withOverallRate(payload);
    data.action = 'save_comments';
    data.comments = comments || '';
    return postMealRating(data);
  }

  function listRatings() {
    var auth = getAuthFields();
    return postMealRating({
      action: 'list',
      type: auth.type,
      hash: auth.hash,
      id: auth.id,
      email: auth.email,
      shop: auth.shop
    });
  }

  window.MealRatingApi = {
    post: postMealRating,
    saveBatch: saveBatch,
    saveComments: saveComments,
    list: listRatings
  };

  function MealRatingPopup() {
    this.root = null;
    this.order = null;
    this.meals = [];
    this.ratings = {};
    this.onConfirm = null;
    this.onClose = null;
    this.lastFocused = null;
    this._boundKeydown = null;
    this._saving = false;
    this._animating = false;
    this.listRows = [];
    this.lockOrder = false;
    this.orderQueue = [];
    this.orderIndex = 0;
  }

  MealRatingPopup.prototype.init = function () {
    if (this.root) return true;

    this.root = document.getElementById('meal-rating-popup');
    if (!this.root) return false;

    this.gridEl = this.root.querySelector('[data-meal-rating-grid]');
    this.confirmBtn = this.root.querySelector('[data-meal-rating-confirm]');
    this.titleEl = this.root.querySelector('[data-meal-rating-title]');
    this.subtitleEl = this.root.querySelector('[data-meal-rating-subtitle]');
    this.deliveryDateEl = this.root.querySelector('[data-meal-rating-delivery-date]');
    this.orderIndexEl = this.root.querySelector('[data-meal-rating-order-index]');
    this.prevBtn = this.root.querySelector('[data-meal-rating-prev]');
    this.nextBtn = this.root.querySelector('[data-meal-rating-next]');

    var self = this;

    this.root.addEventListener('click', function (event) {
      if (event.target.closest('[data-meal-rating-dismiss]')) {
        self.hide();
        return;
      }

      var thumb = event.target.closest('[data-meal-rating-thumb]');
      if (thumb) {
        self.setRating(thumb.getAttribute('data-meal-key'), thumb.getAttribute('data-rate'));
        return;
      }

      if (event.target.closest('[data-meal-rating-confirm]')) {
        self.confirm();
        return;
      }

      if (event.target.closest('[data-meal-rating-prev]')) {
        self.showQueueIndex(self.orderIndex - 1, { animate: true });
        return;
      }

      if (event.target.closest('[data-meal-rating-next]')) {
        self.showQueueIndex(self.orderIndex + 1, { animate: true });
      }
    });

    this._boundKeydown = function (event) {
      if (!self.isOpen()) return;
      if (event.key === 'Escape') {
        self.hide();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        self.showQueueIndex(self.orderIndex - 1, { animate: true });
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        self.showQueueIndex(self.orderIndex + 1, { animate: true });
      }
    };
    document.addEventListener('keydown', this._boundKeydown);

    return true;
  };

  MealRatingPopup.prototype.isOpen = function () {
    return !!this.root && this.root.classList.contains('is-open');
  };

  MealRatingPopup.prototype.buildCard = function (meal, index) {
    var key = mealKey(meal, index);
    var article = document.createElement('article');
    article.className = 'meal-rating-popup__card';
    article.setAttribute('data-meal-key', key);

    if (meal.image) {
      var media = document.createElement('div');
      media.className = 'meal-rating-popup__card-media';

      var image = document.createElement('img');
      image.className = 'meal-rating-popup__card-image';
      image.src = meal.image;
      image.alt = meal.title || '';
      image.loading = 'lazy';
      media.appendChild(image);
      article.appendChild(media);
    }

    var body = document.createElement('div');
    body.className = 'meal-rating-popup__card-body';

    var heading = document.createElement('div');
    heading.className = 'meal-rating-popup__card-heading';

    var title = document.createElement('h3');
    title.className = 'meal-rating-popup__card-title';
    title.textContent = meal.title || '';
    heading.appendChild(title);

    if (meal.subtitle) {
      var subtitle = document.createElement('p');
      subtitle.className = 'meal-rating-popup__card-subtitle';
      subtitle.textContent = meal.subtitle;
      heading.appendChild(subtitle);
    }

    body.appendChild(heading);

    var thumbs = document.createElement('div');
    thumbs.className = 'meal-rating-popup__thumbs';

    var goodBtn = document.createElement('button');
    goodBtn.type = 'button';
    goodBtn.className = 'meal-rating-popup__thumb meal-rating-popup__thumb--good';
    goodBtn.setAttribute('data-meal-rating-thumb', '');
    goodBtn.setAttribute('data-meal-key', key);
    goodBtn.setAttribute('data-rate', 'good');
    goodBtn.setAttribute('aria-label', 'Rate ' + (meal.title || 'meal') + ' good');
    goodBtn.innerHTML = THUMB_UP_SVG;
    thumbs.appendChild(goodBtn);

    var badBtn = document.createElement('button');
    badBtn.type = 'button';
    badBtn.className = 'meal-rating-popup__thumb meal-rating-popup__thumb--bad';
    badBtn.setAttribute('data-meal-rating-thumb', '');
    badBtn.setAttribute('data-meal-key', key);
    badBtn.setAttribute('data-rate', 'bad');
    badBtn.setAttribute('aria-label', 'Rate ' + (meal.title || 'meal') + ' bad');
    badBtn.innerHTML = THUMB_DOWN_SVG;
    thumbs.appendChild(badBtn);

    body.appendChild(thumbs);

    article.appendChild(body);
    return article;
  };

  MealRatingPopup.prototype.render = function () {
    this.gridEl.innerHTML = '';
    var self = this;
    this.meals.forEach(function (meal, index) {
      self.gridEl.appendChild(self.buildCard(meal, index));
    });
    this.syncThumbUi();
  };

  MealRatingPopup.prototype.syncThumbUi = function () {
    var self = this;
    this.gridEl.querySelectorAll('[data-meal-rating-thumb]').forEach(function (btn) {
      var key = btn.getAttribute('data-meal-key');
      var rate = btn.getAttribute('data-rate');
      btn.classList.toggle('is-active', self.ratings[key] === rate);
    });
  };

  MealRatingPopup.prototype.setRating = function (key, rate) {
    if (!key || (rate !== 'good' && rate !== 'bad')) return;

    // Clicking the active thumb again clears it (allows "just OK" / no rating).
    if (this.ratings[key] === rate) {
      delete this.ratings[key];
    } else {
      this.ratings[key] = rate;
    }

    this.syncThumbUi();
  };

  /**
   * Builds the batch payload for the future meal_rating save_batch endpoint.
   * Only meals the customer has rated (good/bad) are included in ratings[].
   *
   * Field names align with the existing single-meal save API where possible
   * (oid, cid, pid, vid, product_type, rate) so backend can reuse the same columns.
   */
  MealRatingPopup.prototype.buildBatchPayload = function () {
    var self = this;
    var order = this.order || {};
    var auth = getAuthFields();
    var ratings = [];
    var goodCount = 0;
    var badCount = 0;

    this.meals.forEach(function (meal, index) {
      var key = mealKey(meal, index);
      var rate = self.ratings[key];
      if (rate !== 'good' && rate !== 'bad') return;

      if (rate === 'good') goodCount += 1;
      if (rate === 'bad') badCount += 1;

      ratings.push({
        pid: meal.product_id || '',
        vid: meal.variant_id || '',
        sku: meal.sku || '',
        title: meal.title || '',
        product_type: meal.type || 'meal',
        rate: rate
      });
    });

    var ratedCount = goodCount + badCount;

    return {
      action: 'save_batch',
      rate: badCount > 0 ? 'bad' : goodCount > 0 ? 'good' : '',

      // Customer authentication (same as existing meal_rating list/save)
      type: auth.type,
      hash: auth.hash,
      id: auth.id || order.customer_id || '',
      email: auth.email,
      shop: auth.shop,

      // Order-level
      oid: order.order_id || '',
      order_name: order.order_name || '',
      cid: order.customer_id || auth.id || '',
      delivery_date: order.delivery_date || '',

      // Rated meals only
      ratings: ratings,

      // Frontend helpers for post-confirm journey (optional for backend)
      good_count: goodCount,
      bad_count: badCount,
      rated_count: ratedCount,
      total_count: this.meals.length,
      good_ratio: ratedCount > 0 ? goodCount / ratedCount : 0
    };
  };

  MealRatingPopup.prototype.setConfirmBusy = function (isBusy) {
    if (!this.confirmBtn) return;
    this.confirmBtn.disabled = !!isBusy;
    this.confirmBtn.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  };

  MealRatingPopup.prototype.confirm = function () {
    if (this._saving) return;

    var payload = this.buildBatchPayload();
    var self = this;

    console.log('[MealRatingPopup] save_batch payload', payload);

    if (!payload.rated_count) {
      console.warn('[MealRatingPopup] confirm skipped — no meals rated');
      return;
    }

    if (!window.MealRatingApi || typeof window.MealRatingApi.saveBatch !== 'function') {
      console.error('[MealRatingPopup] MealRatingApi.saveBatch is not available');
      return;
    }

    this._saving = true;
    this.setConfirmBusy(true);

    window.MealRatingApi.saveBatch(payload)
      .then(function (data) {
        console.log('[MealRatingPopup] save_batch response', data);

        if (typeof window.getRatingList === 'function') {
          window.getRatingList();
        }

        if (typeof self.onConfirm === 'function') {
          self.onConfirm(payload, data);
        }

        document.dispatchEvent(
          new CustomEvent('meal-rating-popup:confirm', { detail: payload })
        );

        self.hide({ skipOnClose: true });
      })
      .catch(function (error) {
        console.error('[MealRatingPopup] save_batch failed', error);
      })
      .then(function () {
        self._saving = false;
        self.setConfirmBusy(false);
      });
  };

  MealRatingPopup.prototype.show = function (options) {
    if (!this.init()) return;

    var config = options || {};
    var meals = Array.isArray(config.meals) ? config.meals : [];
    var fallback = null;

    if (config.order && meals.length) {
      fallback = { order: config.order, meals: meals };
    }

    this.onConfirm = typeof config.onConfirm === 'function' ? config.onConfirm : null;
    this.onClose = typeof config.onClose === 'function' ? config.onClose : null;
    this.order = null;
    this.meals = [];
    this.ratings = {};
    this.orderQueue = [];
    this.orderIndex = 0;
    this._animating = false;
    this.includeRated = !!config.includeRated;
    this.lockOrder = !!config.lockOrder;
    this.listRows = [];
    this.allRatedFallback = false;
    this.seedRating = config.seedRating || null;
    this._customHeader = config.title !== undefined || config.subtitle !== undefined;

    if (config.title !== undefined && this.titleEl) {
      this.titleEl.textContent = config.title;
    } else if (this.titleEl) {
      this.titleEl.textContent = DEFAULT_TITLE;
    }
    if (config.subtitle !== undefined && this.subtitleEl) {
      this.subtitleEl.textContent = config.subtitle;
      this.subtitleEl.style.display = config.subtitle ? '' : 'none';
    } else if (this.subtitleEl) {
      this.subtitleEl.textContent = DEFAULT_SUBTITLE;
      this.subtitleEl.style.display = '';
    }

    this.lastFocused = document.activeElement;
    this.root.classList.add('is-open');
    this.root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('meal-rating-popup-open');

    var closeButton = this.root.querySelector('.meal-rating-popup__close');
    if (closeButton) closeButton.focus();

    document.dispatchEvent(new CustomEvent('meal-rating-popup:show'));

    if (this.gridEl) {
      this.gridEl.innerHTML = '<p class="meal-rating-popup__loading">Loading orders…</p>';
    }

    var self = this;
    var afterQueue = function (queue) {
      if (!queue.length) {
        console.warn('[MealRatingPopup] show() — no rateable orders');
        if (self.gridEl) {
          self.gridEl.innerHTML = '<p class="meal-rating-popup__loading">No orders available to rate.</p>';
        }
        self.syncNav();
        return;
      }

      console.log('[MealRatingPopup] order queue', {
        lock_order: self.lockOrder,
        include_rated: self.includeRated,
        all_rated_fallback: !!self.allRatedFallback,
        count: queue.length,
        orders: queue.map(function (item) {
          return item.order && item.order.order_name;
        })
      });

      self.applyHeaderCopy();
      self.showQueueIndex(0, { force: true });
    };

    if (this.lockOrder && fallback) {
      this.orderQueue = [fallback];
      this.fetchListRows().then(function () {
        afterQueue(self.orderQueue);
      });
      return;
    }

    this.loadUnratedQueue(fallback, this.includeRated).then(afterQueue);
  };

  MealRatingPopup.prototype.applyHeaderCopy = function () {
    if (this._customHeader) return;

    var allSet = !!this.allRatedFallback;
    if (this.titleEl) {
      this.titleEl.textContent = allSet ? ALL_SET_TITLE : DEFAULT_TITLE;
    }
    if (this.subtitleEl) {
      this.subtitleEl.textContent = allSet ? ALL_SET_SUBTITLE : DEFAULT_SUBTITLE;
      this.subtitleEl.style.display = '';
    }
  };

  MealRatingPopup.prototype.getRateableOrdersCatalog = function () {
    if (Array.isArray(window.rateableOrders) && window.rateableOrders.length) {
      return window.rateableOrders.slice();
    }

    var fromDom = this.getAllOrdersFromAccountPage();
    if (fromDom.length) return fromDom;

    var last = this.getLastRateableOrder();
    return last ? [last] : [];
  };

  MealRatingPopup.prototype.loadUnratedQueue = function (fallback, includeRated) {
    var self = this;
    var catalog = this.getRateableOrdersCatalog();
    if (fallback && fallback.order && fallback.meals && fallback.meals.length) {
      var fallbackId = String(fallback.order.order_id || '');
      var already = catalog.some(function (item) {
        return item.order && String(item.order.order_id) === fallbackId;
      });
      if (!already) catalog.unshift(fallback);
    }

    var applyFilter = function (ratedIds) {
      if (includeRated) {
        self.allRatedFallback = false;
        return catalog.filter(function (item) {
          return item && item.meals && item.meals.length;
        });
      }

      var unrated = catalog.filter(function (item) {
        var id = item && item.order && String(item.order.order_id);
        return id && !ratedIds[id] && item.meals && item.meals.length;
      });
      if (unrated.length) {
        self.allRatedFallback = false;
        return unrated;
      }
      self.allRatedFallback = true;
      if (fallback && fallback.meals && fallback.meals.length) return [fallback];
      return catalog.slice(0, 1);
    };

    if (!window.MealRatingApi || typeof window.MealRatingApi.list !== 'function') {
      this.orderQueue = applyFilter({});
      return Promise.resolve(this.orderQueue);
    }

    return window.MealRatingApi.list()
      .then(function (data) {
        var ratedIds = {};
        var rows = (data && data.data) || [];
        self.listRows = rows;
        rows.forEach(function (row) {
          if (row && row.order_id != null) ratedIds[String(row.order_id)] = true;
        });
        self.orderQueue = applyFilter(ratedIds);
        return self.orderQueue;
      })
      .catch(function (error) {
        console.warn('[MealRatingPopup] list failed — showing all rateable orders', error);
        self.orderQueue = applyFilter({});
        return self.orderQueue;
      });
  };

  MealRatingPopup.prototype.showQueueIndex = function (index, options) {
    if (!this.orderQueue.length) return;
    if (index < 0 || index >= this.orderQueue.length) return;

    var opts = options || {};
    if (index === this.orderIndex && this.meals.length && !opts.force) return;
    if (this._animating) return;

    var shouldAnimate = !!opts.animate && this.meals.length;
    var direction = index > this.orderIndex ? 'next' : 'prev';
    var self = this;

    var applyOrder = function () {
      var item = self.orderQueue[index];
      self.orderIndex = index;
      self.order = item.order || null;
      self.meals = (item.meals || []).map(normalizeMeal);
      self.ratings = self.buildRatingsFromList(self.order, self.meals);
      self.applySeedRating();

      console.log('[MealRatingPopup] showing order', {
        index: index + 1,
        total: self.orderQueue.length,
        order_id: self.order && self.order.order_id,
        order_name: self.order && self.order.order_name,
        delivery_date: self.order && self.order.delivery_date,
        meal_count: self.meals.length
      });

      self.updateDeliveryDate();
      self.render();
      self.syncNav();
    };

    if (!shouldAnimate || !this.gridEl) {
      applyOrder();
      return;
    }

    this._animating = true;
    this.gridEl.classList.remove('is-enter-next', 'is-enter-prev', 'is-enter-active');
    this.gridEl.classList.add(direction === 'next' ? 'is-leave-next' : 'is-leave-prev');
    if (this.deliveryDateEl) {
      this.deliveryDateEl.classList.remove('is-enter-next', 'is-enter-prev', 'is-enter-active');
      this.deliveryDateEl.classList.add(direction === 'next' ? 'is-leave-next' : 'is-leave-prev');
    }

    var finishEnter = function () {
      self.gridEl.classList.remove('is-enter-next', 'is-enter-prev');
      self.gridEl.classList.add('is-enter-active');
      if (self.deliveryDateEl) {
        self.deliveryDateEl.classList.remove('is-enter-next', 'is-enter-prev');
        self.deliveryDateEl.classList.add('is-enter-active');
      }
      window.setTimeout(function () {
        if (self.gridEl) {
          self.gridEl.classList.remove('is-enter-active');
        }
        if (self.deliveryDateEl) {
          self.deliveryDateEl.classList.remove('is-enter-active');
        }
        self._animating = false;
      }, 300);
    };

    window.setTimeout(function () {
      applyOrder();
      if (!self.gridEl) {
        self._animating = false;
        return;
      }
      self.gridEl.classList.remove('is-leave-next', 'is-leave-prev');
      self.gridEl.classList.add(direction === 'next' ? 'is-enter-next' : 'is-enter-prev');
      if (self.deliveryDateEl) {
        self.deliveryDateEl.classList.remove('is-leave-next', 'is-leave-prev');
        self.deliveryDateEl.classList.add(direction === 'next' ? 'is-enter-next' : 'is-enter-prev');
      }
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(finishEnter);
      });
    }, 280);
  };

  MealRatingPopup.prototype.fetchListRows = function () {
    var self = this;
    this.listRows = [];
    if (!window.MealRatingApi || typeof window.MealRatingApi.list !== 'function') {
      return Promise.resolve([]);
    }
    return window.MealRatingApi.list()
      .then(function (data) {
        self.listRows = (data && data.data) || [];
        return self.listRows;
      })
      .catch(function (error) {
        console.warn('[MealRatingPopup] list failed', error);
        self.listRows = [];
        return [];
      });
  };

  MealRatingPopup.prototype.buildRatingsFromList = function (order, meals) {
    var ratings = {};
    var rows = this.listRows || [];
    var orderId = order && String(order.order_id);
    if (!orderId || !meals || !meals.length) return ratings;

    meals.forEach(function (meal, index) {
      var key = mealKey(meal, index);
      var productId = String(meal.product_id || '');
      var variantId = String(meal.variant_id || '');
      var match = null;

      for (var i = 0; i < rows.length; i += 1) {
        var row = rows[i];
        if (!row || String(row.order_id) !== orderId) continue;
        if (productId && String(row.product_id) === productId) {
          match = row;
          break;
        }
        if (!match && variantId && String(row.variant_id) === variantId) {
          match = row;
        }
      }

      if (!match) return;
      if (match.score == 1) ratings[key] = 'good';
      else if (match.score <= 0) ratings[key] = 'bad';
    });

    return ratings;
  };

  MealRatingPopup.prototype.applySeedRating = function () {
    var seed = this.seedRating;
    if (!seed || (seed.rate !== 'good' && seed.rate !== 'bad')) return;
    if (!this.meals || !this.meals.length) return;

    var index = typeof seed.meal_index === 'number' ? seed.meal_index : -1;
    if (index >= 0 && index < this.meals.length) {
      this.ratings[mealKey(this.meals[index], index)] = seed.rate;
      return;
    }

    var productId = seed.product_id != null ? String(seed.product_id) : '';
    var variantId = seed.variant_id != null ? String(seed.variant_id) : '';
    var i;
    for (i = 0; i < this.meals.length; i += 1) {
      var meal = this.meals[i];
      if (productId && String(meal.product_id || '') === productId) {
        this.ratings[mealKey(meal, i)] = seed.rate;
        return;
      }
      if (!productId && variantId && String(meal.variant_id || '') === variantId) {
        this.ratings[mealKey(meal, i)] = seed.rate;
        return;
      }
    }
  };

  MealRatingPopup.prototype.updateDeliveryDate = function () {
    if (!this.deliveryDateEl) return;
    var deliveryLabel = formatDeliveryDate((this.order && this.order.delivery_date) || '');

    this.deliveryDateEl.textContent = '';
    if (!deliveryLabel) {
      this.deliveryDateEl.hidden = true;
      return;
    }

    var label = document.createElement('span');
    label.className = 'meal-rating-popup__delivery-label';
    label.textContent = 'Delivery date';

    var value = document.createElement('span');
    value.className = 'meal-rating-popup__delivery-value';
    value.textContent = deliveryLabel;

    this.deliveryDateEl.appendChild(label);
    this.deliveryDateEl.appendChild(value);
    this.deliveryDateEl.hidden = false;
  };

  MealRatingPopup.prototype.syncNav = function () {
    var total = this.orderQueue.length;
    var showNav = !this.lockOrder && total > 1;

    if (this.prevBtn) {
      this.prevBtn.hidden = !showNav;
      this.prevBtn.disabled = this.orderIndex <= 0;
    }
    if (this.nextBtn) {
      this.nextBtn.hidden = !showNav;
      this.nextBtn.disabled = this.orderIndex >= total - 1;
    }
    if (this.orderIndexEl) {
      if (showNav) {
        this.orderIndexEl.hidden = false;
        this.orderIndexEl.textContent =
          (this.includeRated ? 'Order ' : 'Unrated order ') +
          (this.orderIndex + 1) +
          ' of ' +
          total;
      } else {
        this.orderIndexEl.hidden = true;
        this.orderIndexEl.textContent = '';
      }
    }
  };

  MealRatingPopup.prototype.hide = function (options) {
    if (!this.root) return;

    var opts = options || {};
    var wasOpen = this.isOpen();

    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('meal-rating-popup-open');

    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }

    if (wasOpen && !opts.skipOnClose && typeof this.onClose === 'function') {
      this.onClose();
    }

    document.dispatchEvent(new CustomEvent('meal-rating-popup:hide'));
  };

  /**
   * Preferred source for reusable MealRatingPopup callers.
   * 1) window.lastRateableOrder (Liquid, all account pages via account-header)
   * 2) DOM Order History fallback (dashboard only; never upcoming charge rows)
   */
  MealRatingPopup.prototype.getLastRateableOrder = function () {
    var injected = window.lastRateableOrder;
    if (
      injected &&
      injected.order &&
      Array.isArray(injected.meals) &&
      injected.meals.length
    ) {
      console.log('[MealRatingPopup] getLastRateableOrder → using window.lastRateableOrder', {
        source: injected.source || 'window.lastRateableOrder',
        order_id: injected.order.order_id,
        order_name: injected.order.order_name,
        meal_count: injected.meals.length
      });
      return {
        order: injected.order,
        meals: injected.meals
      };
    }

    console.warn(
      '[MealRatingPopup] getLastRateableOrder → window.lastRateableOrder missing/empty; trying DOM Order History fallback'
    );
    var fromDom = this.getLastOrderFromAccountPage();
    if (fromDom) {
      console.log('[MealRatingPopup] getLastRateableOrder → DOM fallback OK', {
        order_id: fromDom.order.order_id,
        order_name: fromDom.order.order_name,
        meal_count: fromDom.meals.length
      });
    } else {
      console.warn('[MealRatingPopup] getLastRateableOrder → no last order available on this page');
    }
    return fromDom;
  };

  /**
   * Reads the latest past Shopify order from Order History on /account.
   *
   * Only matches `.order-item[id^="order_"]` with `.item-meal[data-pid]`, because the
   * subscription page reuses `.account-orders__list .order-item` / `.item-meal` for the
   * UPCOMING charge (JS-built rows with no data-* ids). Without this guard the popup
   * would rate the meals the customer just selected instead of their last order.
   *
   * Returns null when no past order markup is present on the page.
   */
  MealRatingPopup.prototype.getLastOrderFromAccountPage = function () {
    var all = this.getAllOrdersFromAccountPage();
    return all.length ? all[0] : null;
  };

  MealRatingPopup.prototype.getAllOrdersFromAccountPage = function () {
    var orderEls = document.querySelectorAll('.order-item[id^="order_"]');
    var orders = [];

    orderEls.forEach(function (orderEl) {
      if (!orderEl.querySelector('.item-meal[data-pid]')) return;

      var order_id = orderEl.id.replace(/^order_/, '') || null;
      var orderHeading = orderEl.querySelector('h4');
      var order_name = orderHeading ? orderHeading.textContent.replace(/^ORDER\s+/i, '').trim() : '';
      var delivery_date = orderEl.getAttribute('data-delivery_date') || '';
      var meals = [];

      orderEl.querySelectorAll('.item-meal[data-pid]').forEach(function (item) {
        var productId = item.getAttribute('data-pid');
        if (!productId) return;

        var img = item.querySelector('.item-meal__img img');
        var qtyEl = item.querySelector('.item-meal__quantity');
        var qtyText = qtyEl ? qtyEl.textContent.replace(/[^\d]/g, '') : '';

        meals.push({
          product_id: productId,
          variant_id: item.getAttribute('data-vid') || '',
          sku: item.getAttribute('data-sku') || '',
          title: item.getAttribute('data-title') || '',
          subtitle: item.getAttribute('data-subtitle') || '',
          image: img ? img.getAttribute('src') : '',
          type: item.getAttribute('data-type') || 'meal',
          quantity: qtyText ? parseInt(qtyText, 10) : 1,
          customer_id: item.getAttribute('data-cid') || '',
          delivery_date: item.getAttribute('data-delivery_date') || delivery_date,
          order_id: item.getAttribute('data-id') || order_id
        });
      });

      if (!meals.length) return;

      orders.push({
        order: {
          order_id: order_id,
          order_name: order_name,
          customer_id: meals[0].customer_id || '',
          delivery_date: delivery_date
        },
        meals: meals
      });
    });

    return orders;
  };

  window.MealRatingPopup = new MealRatingPopup();
})();
